import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./PostReports.css";

class PostReports extends Component {
  state = {
    reports: [],
    selectedReport: null,
    deleteId: null,
    error: "",
    loading: false,
    deleting: false,
    searchTerm: "",
    statusFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchReports();
  }

  getAuthConfig = () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    };
  };

  fetchReports = async () => {
    this.setState({ loading: true, error: "" });

    try {
      const res = await axios.get("posts/post_reports", this.getAuthConfig());
      const data = res.data?.data || [];

      this.setState({
        reports: Array.isArray(data) ? data : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load post reports.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (e) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", currentPage: 1 });
  };

  openViewModal = (report) => {
    this.setState({ selectedReport: report }, () => {
      const modalElement = document.getElementById("reportDetailModal");
      if (modalElement && window.bootstrap) {
        const modal = new window.bootstrap.Modal(modalElement);
        modal.show();
      }
    });
  };

  handleToggleStatus = async (report) => {
    const newStatus = Number(report.post_report_status) === 1 ? 0 : 1;

    try {
      await axios.put(
        `posts/post_reports/${report.id}`,
        { post_report_status: newStatus },
        this.getAuthConfig()
      );

      await this.fetchReports();

      Swal.fire({
        icon: "success",
        title:
          newStatus === 0
            ? "Report marked as resolved!"
            : "Report marked as pending!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to update report status.",
        text: err.response?.data?.message || "Please try again.",
      });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalElement = document.getElementById("deleteReportModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`posts/post_reports/${deleteId}`, this.getAuthConfig());
      await this.fetchReports();

      const modalElement = document.getElementById("deleteReportModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Post report deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete report.",
        text: err.response?.data?.message || "Please try again.",
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  getFilteredReports = () => {
    const { reports, searchTerm, statusFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return reports.filter((item) => {
      const typeText = (item.report_type || "").toLowerCase();
      const descText = (item.report_description || "").toLowerCase();
      const postIdText = item.post_id?.toString() || "";
      const status = item.post_report_status?.toString() || "1";

      const matchesSearch =
        !keyword ||
        typeText.includes(keyword) ||
        descText.includes(keyword) ||
        postIdText.includes(keyword);

      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.max(
      1,
      Math.ceil(this.getFilteredReports().length / itemsPerPage)
    );
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  render() {
    const {
      reports,
      selectedReport,
      error,
      loading,
      deleting,
      searchTerm,
      statusFilter,
      currentPage,
      itemsPerPage,
    } = this.state;

    const filteredReports = this.getFilteredReports();
    const totalPages = Math.max(
      1,
      Math.ceil(filteredReports.length / itemsPerPage)
    );
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentReports = filteredReports.slice(
      startIndex,
      startIndex + itemsPerPage
    );
    const startRecord = filteredReports.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredReports.length);

    const pendingCount = reports.filter(
      (r) => r.post_report_status?.toString() === "1"
    ).length;
    const resolvedCount = reports.length - pendingCount;
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container reports-page">
        <div className="pc-content">
          <div className="reports-shell">
            {/* Header */}
            <div className="reports-topbar">
              <div>
                <div className="reports-breadcrumb">
                  <span>Management</span>
                  <i className="ph ph-caret-right" />
                  <strong>Post Reports</strong>
                </div>
                <h2 className="reports-title">Post Reports</h2>
                <p className="reports-subtitle">
                  Review property listing issues and complaints submitted by customers.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={this.fetchReports}
              >
                <i className="ph ph-arrow-clockwise" />
                <span>Refresh Reports</span>
              </button>
            </div>

            {/* Summary Grid */}
            <div className="reports-summary-grid">
              <div className="reports-summary-card">
                <div className="reports-summary-icon blue">
                  <i className="ph ph-flag" />
                </div>
                <div>
                  <span className="summary-label">Total Reports</span>
                  <strong>{reports.length}</strong>
                </div>
              </div>

              <div className="reports-summary-card">
                <div className="reports-summary-icon orange">
                  <i className="ph ph-warning-circle" />
                </div>
                <div>
                  <span className="summary-label">Pending Review</span>
                  <strong>{pendingCount}</strong>
                </div>
              </div>

              <div className="reports-summary-card">
                <div className="reports-summary-icon green">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Resolved</span>
                  <strong>{resolvedCount}</strong>
                </div>
              </div>
            </div>

            {/* Main Card */}
            <div className="reports-card">
              <div className="reports-card-header">
                <div>
                  <h4>Reported Listings List</h4>
                  <p>
                    {filteredReports.length} result
                    {filteredReports.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="reports-toolbar">
                <div className="reports-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="reports-search"
                    placeholder="Search type, description, or post ID..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="reports-search-clear"
                      onClick={() =>
                        this.setState({ searchTerm: "", currentPage: 1 })
                      }
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="reports-filter-group">
                  <label htmlFor="reportStatusFilter">Status</label>
                  <select
                    id="reportStatusFilter"
                    className="reports-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Pending Review</option>
                    <option value="0">Resolved</option>
                  </select>
                </div>

                <div className="reports-filter-group">
                  <label htmlFor="reportRows">Rows</label>
                  <select
                    id="reportRows"
                    className="reports-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all") && (
                  <button
                    type="button"
                    className="reports-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="reports-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchReports}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="reports-loading">
                  <div
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Loading reports...</span>
                </div>
              ) : (
                <>
                  <div className="reports-table-wrap">
                    <table className="reports-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th>Post</th>
                          <th>Customer</th>
                          <th>Report Type</th>
                          <th>Date</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentReports.length ? (
                          currentReports.map((item, index) => {
                            const isPending =
                              item.post_report_status === "1" ||
                              item.post_report_status === 1;
                            return (
                              <tr key={item.id}>
                                <td className="col-number table-index">
                                  {startIndex + index + 1}
                                </td>
                                <td>
                                  <a
                                    href={`/listing/${item.post_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="post-link-cell"
                                  >
                                    <span>{item.title}</span>
                                  </a>
                                </td>
                                <td>
                                  {item.customer_name}<br></br>
                                  {item.customer_phone}<br></br>
                                  </td>
                                <td>
                                  <span className="report-type-pill">
                                    {item.report_type || "General Issue"}
                                  </span>
                                </td>
                                <td>{item.post_report_date}</td>
                                <td className="col-status">
                                  <span
                                    className={`reports-status-badge ${
                                      isPending ? "active" : "resolved"
                                    }`}
                                  >
                                    <span className="status-dot" />
                                    {isPending ? "Pending Review" : "Resolved"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="reports-actions">
                                    <button
                                      type="button"
                                      className="reports-action-btn view"
                                      onClick={() => this.openViewModal(item)}
                                      title="View description"
                                    >
                                      <i className="ph ph-eye" />
                                    </button>
                                    <button
                                      type="button"
                                      className="reports-action-btn resolve"
                                      onClick={() => this.handleToggleStatus(item)}
                                      title={
                                        isPending
                                          ? "Mark as Resolved"
                                          : "Mark as Pending"
                                      }
                                    >
                                      <i
                                        className={
                                          isPending
                                            ? "ph ph-check"
                                            : "ph ph-arrow-u-up-left"
                                        }
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      className="reports-action-btn delete"
                                      onClick={() => this.confirmDelete(item.id)}
                                      title="Delete report"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7">
                              <div className="reports-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-flag" />
                                </div>
                                <h5>No post reports found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all"
                                    ? "Try changing your search query or filters."
                                    : "There are currently no property reports submitted."}
                                </p>
                                {(searchTerm || statusFilter !== "all") && (
                                  <button
                                    type="button"
                                    className="reports-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredReports.length > 0 && (
                    <div className="reports-pagination-wrap">
                      <div className="reports-pagination-info">
                        Showing <strong>{startRecord}</strong> to{" "}
                        <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredReports.length}</strong> reports
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Reports pagination">
                          <ul className="reports-pagination">
                            <li
                              className={
                                safeCurrentPage === 1 ? "disabled" : ""
                              }
                            >
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>
                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="ellipsis"
                                >
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={
                                    safeCurrentPage === page ? "active" : ""
                                  }
                                >
                                  <button
                                    type="button"
                                    onClick={() => this.goToPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li
                              className={
                                safeCurrentPage === totalPages ? "disabled" : ""
                              }
                            >
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* View Description Modal */}
        <div className="modal fade" id="reportDetailModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content reports-modal">
              <div className="modal-header reports-modal-header">
                <div>
                  <span className="reports-modal-kicker">Report Details</span>
                  <h5 className="modal-title">
                    Report #{selectedReport?.id}
                  </h5>
                </div>
                <button
                  type="button"
                  className="reports-modal-close"
                  data-bs-dismiss="modal"
                >
                  <i className="ph ph-x" />
                </button>
              </div>

              <div className="modal-body reports-modal-body">
                
                <div className="report-detail-group">
                  <label>Customer Name</label>
                  <p>{selectedReport?.customer_name}</p>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="report-detail-group">
                      <label>Customer Phone</label>
                      <p>{selectedReport?.customer_phone}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="report-detail-group">
                      <label>Customer ID</label>
                      <p>BK-CUS-{selectedReport?.customer_id}</p>
                    </div>
                  </div>
                </div>
                
                <div className="report-detail-group">
                  <label>Host Name</label>
                  <p>{selectedReport?.host_name}</p>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="report-detail-group">
                      <label>Host Phone</label>
                      <p>{selectedReport?.host_phone}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="report-detail-group">
                      <label>HOST ID</label>
                      <p>BK-HOST-{selectedReport?.host_id}</p>
                    </div>
                  </div>
                </div>

                <div className="report-detail-group">
                  <label><b style={{ color: 'black' }}>Report Type / Reason</b></label>
                  <p>{selectedReport?.report_type}</p>
                </div>

                <div className="report-detail-group">
                  <label><b style={{ color: 'black' }}>Report Description</b></label>
                  <p>{selectedReport?.report_description || "No description provided."}</p>
                </div>

                <div className="report-detail-group mb-0">
                  <label>Date Submitted</label>
                  <p>{selectedReport?.post_report_date}</p>
                </div>
              </div>

              <div className="modal-footer reports-modal-footer">
                <button
                  type="button"
                  className="bookly-cancel-btn"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <div className="modal fade" id="deleteReportModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Report?</h5>
                <p>
                  This post report will be permanently removed. This action cannot
                  be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PostReports;